// src/components/ArtworkDossierExport.jsx
//
// Génère un dossier PDF complet pour une œuvre unique :
//   1. Page de garde  — image principale, titre, artiste, année, médium
//   2. Fiche détaillée — tous les champs pertinents en tableau
//   3. Historique      — interventions depuis la table `maintenance`
//   4. Estimations     — valeurs enregistrées sur l'œuvre
//   5. Documents       — liste des pièces jointes (invoice, cert, etc.)

import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { supabase } from "../lib/supabaseClient";

// ── Helpers ──────────────────────────────────────────────────────────────────

const HTML_ENTITIES = { "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"', "&#39;": "'", "&nbsp;": " ", "&apos;": "'" };
const decodeHtml = (s) => String(s).replace(/&[a-zA-Z0-9#]+;/g, (m) => HTML_ENTITIES[m] ?? m);
const fmt = (v) => (v == null || v === "" ? "—" : decodeHtml(v));
const fmtDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("fr-FR"); } catch { return v; }
};
const fmtNum = (v, suffix = " €") =>
  v == null ? "—" : `${Number(v).toLocaleString("fr-FR")}${suffix}`;

const GOLD = [201, 169, 97];
const BLACK = [26, 26, 26];
const GRAY = [138, 138, 138];
const LIGHT = [249, 247, 244];

// Charge une image depuis une URL et la convertit en base64
async function urlToBase64(url) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ── Sections PDF ─────────────────────────────────────────────────────────────

function addPageGarde(doc, artwork, imgData) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // Fond
  doc.setFillColor(...LIGHT);
  doc.rect(0, 0, W, H, "F");

  // Filet doré en haut
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 2, "F");

  // Image principale
  if (imgData) {
    const imgH = 120;
    const imgW = W - 40;
    try { doc.addImage(imgData, "JPEG", 20, 20, imgW, imgH); } catch {}
  }

  const textY = imgData ? 155 : 60;

  // Artiste
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...BLACK);
  doc.text(fmt(artwork.artist), W / 2, textY, { align: "center" });

  // Titre
  doc.setFont("helvetica", "italic");
  doc.setFontSize(15);
  doc.setTextColor(...GRAY);
  doc.text(fmt(artwork.title), W / 2, textY + 10, { align: "center" });

  // Année · médium
  const sub = [artwork.year, artwork.medium].filter(Boolean).join(" · ");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(sub || "—", W / 2, textY + 20, { align: "center" });

  // Filet doré bas
  doc.setFillColor(...GOLD);
  doc.rect(0, H - 2, W, 2, "F");

  // Pied de page
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.setFont("helvetica", "normal");
  doc.text(
    `Dossier généré par Artkiv — ${new Date().toLocaleDateString("fr-FR")} — Document confidentiel`,
    W / 2,
    H - 8,
    { align: "center" }
  );
}

function addFicheDetaillee(doc, artwork) {
  doc.addPage();
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 2, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...BLACK);
  doc.text("Fiche de l'œuvre", 20, 18);

  const rows = [
    ["Artiste", fmt(artwork.artist)],
    ["Titre", fmt(artwork.title)],
    ["Année", fmt(artwork.year)],
    ["Médium / technique", fmt(artwork.medium)],
    ["Dimensions", buildDimensions(artwork)],
    ["État de conservation", fmt(artwork.condition)],
    ["N° inventaire", fmt(artwork.inventory_number)],
    ["Type d'œuvre", fmt(artwork.typeOfArt)],
    ["Statut", fmt(artwork.artwork_status)],
    ["Provenance", fmt(artwork.provenance)],
    ["Acquisition", buildAcquisition(artwork)],
    ["Signature", artwork.signature ? "Oui" : artwork.signature === false ? "Non" : "—"],
    ["Édition", artwork.isEdition ? fmt(artwork.editionInfo) : "Œuvre unique"],
    ["Notes de conservation", fmt(artwork.conditionNotes)],
    ["Description", fmt(artwork.description)],
  ].filter(([, v]) => v !== "—");

  autoTable(doc, {
    startY: 24,
    head: [],
    body: rows,
    styles: { font: "helvetica", fontSize: 10, cellPadding: 4 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 55, textColor: BLACK, fillColor: [245, 243, 240] },
      1: { textColor: BLACK },
    },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    margin: { left: 20, right: 20 },
  });
}

function addHistorique(doc, records) {
  doc.addPage();
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 2, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...BLACK);
  doc.text("Historique de conservation", 20, 18);

  if (!records.length) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...GRAY);
    doc.text("Aucune intervention enregistrée.", 20, 30);
    return;
  }

  const rows = records.map((r) => [
    fmtDate(r.date),
    fmt(r.type),
    fmt(r.contact),
    fmt(r.notes),
    r.cost != null ? fmtNum(r.cost) : "—",
  ]);

  autoTable(doc, {
    startY: 24,
    head: [["Date", "Type", "Intervenant", "Notes", "Coût"]],
    body: rows,
    styles: { font: "helvetica", fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: BLACK, textColor: [255, 255, 255], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [249, 247, 244] },
    margin: { left: 20, right: 20 },
    columnStyles: {
      0: { cellWidth: 24 },
      4: { cellWidth: 22, halign: "right" },
    },
  });
}

function addEstimations(doc, artwork) {
  doc.addPage();
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 2, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...BLACK);
  doc.text("Estimations & valorisation", 20, 18);

  const rows = [
    ["Valeur estimée courante", fmtNum(artwork.current_estimated_value ?? artwork.estimated_value)],
    ["Valeur de marché", fmtNum(artwork.valuation_market_value)],
    ["Fourchette basse", fmtNum(artwork.valuation_low ?? artwork.price_low)],
    ["Fourchette haute", fmtNum(artwork.valuation_high ?? artwork.price_high)],
    ["Méthode de valorisation", fmt(artwork.valuation_method)],
    ["Dernière estimation", fmtDate(artwork.last_valuation_date)],
    ["Score de confiance IA", artwork.valuation_confidence_score != null ? `${artwork.valuation_confidence_score} / 100` : "—"],
    ["Facture d'acquisition", artwork.invoice ? "Oui" : "Non"],
    ["Certificat d'authenticité", artwork.certificate_auth || artwork.certificate_authenticity ? "Oui" : "Non"],
    ["Note expert", fmt(artwork.expert_note)],
    ["Date expertise", fmtDate(artwork.expert_date)],
    ["Prix d'acquisition", fmtNum(artwork.acquisition_price)],
    ["Date d'acquisition", fmtDate(artwork.acquisition_date)],
  ].filter(([, v]) => v !== "—");

  autoTable(doc, {
    startY: 24,
    head: [],
    body: rows,
    styles: { font: "helvetica", fontSize: 10, cellPadding: 4 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 70, textColor: BLACK, fillColor: [245, 243, 240] },
      1: { textColor: BLACK },
    },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    margin: { left: 20, right: 20 },
  });
}

function addDocuments(doc, artwork) {
  const docs = [];
  if (artwork.invoice) docs.push(["Facture d'acquisition", "Présente"]);
  if (artwork.certificate_auth) docs.push(["Certificat d'authenticité", "Présent"]);
  if (artwork.certificate_blockchain) docs.push(["Preuve d'authenticité numérique", "Présente"]);
  if (artwork.certificate_authenticity) docs.push(["Attestation d'authenticité", artwork.certificate_authenticity]);
  if (artwork.condition_report) docs.push(["Rapport de conservation", "Disponible"]);
  if (artwork.condition_report_url) docs.push(["Rapport de conservation (URL)", artwork.condition_report_url]);

  if (!docs.length) return;

  doc.addPage();
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 2, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...BLACK);
  doc.text("Documents associés", 20, 18);

  autoTable(doc, {
    startY: 24,
    head: [["Document", "Statut / Référence"]],
    body: docs,
    styles: { font: "helvetica", fontSize: 10, cellPadding: 4 },
    headStyles: { fillColor: BLACK, textColor: [255, 255, 255], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [249, 247, 244] },
    margin: { left: 20, right: 20 },
  });
}

function addPagination(doc) {
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text(`${i} / ${total}`, W - 20, H - 8, { align: "right" });
    // Filet doré bas sur chaque page (sauf page 1 déjà fait)
    if (i > 1) {
      doc.setFillColor(...GOLD);
      doc.rect(0, H - 2, W, 2, "F");
    }
  }
}

// ── Helpers champs ────────────────────────────────────────────────────────────

function buildDimensions(a) {
  const parts = [];
  const h = a.dimensions_height ?? a.height;
  const w = a.dimensions_width ?? a.width;
  const d = a.dimensions_depth ?? a.depth;
  const unit = a.dimensions_unit ?? a.unit ?? "cm";
  if (h && w) parts.push(`${h} × ${w}${d ? ` × ${d}` : ""} ${unit}`);
  else if (a.dimensions) parts.push(a.dimensions);
  return parts.join(", ") || "—";
}

function buildAcquisition(a) {
  const parts = [];
  if (a.acquisition_type) parts.push(a.acquisition_type);
  if (a.acquisition_source) parts.push(a.acquisition_source);
  if (a.acquisition_date) parts.push(fmtDate(a.acquisition_date));
  if (a.purchase_city) parts.push(a.purchase_city);
  if (a.purchase_country) parts.push(a.purchase_country);
  return parts.join(", ") || "—";
}

// ── Composant ─────────────────────────────────────────────────────────────────

export default function ArtworkDossierExport({ artwork, className, label }) {
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!artwork?.id) return;
    setLoading(true);

    try {
      // 1. Charger l'historique maintenance
      const { data: maintenance } = await supabase
        .from("maintenance")
        .select("date, type, contact, notes, cost")
        .eq("artwork_id", artwork.id)
        .order("date", { ascending: false });

      // 2. Charger l'image principale en base64
      const imageUrl = artwork.image_url || (artwork.images?.[0]?.url) || null;
      const imgData = imageUrl ? await urlToBase64(imageUrl) : null;

      // 3. Générer le PDF
      const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });

      addPageGarde(doc, artwork, imgData);
      addFicheDetaillee(doc, artwork);
      addHistorique(doc, maintenance ?? []);
      addEstimations(doc, artwork);
      addDocuments(doc, artwork);
      addPagination(doc);

      // 4. Télécharger
      const safeName = [artwork.artist, artwork.title]
        .filter(Boolean)
        .join("_")
        .replace(/[^a-zA-Z0-9_-]/g, "_")
        .slice(0, 60);
      doc.save(`Dossier_${safeName || artwork.id}.pdf`);

      // 5. Marquer le dossier comme généré
      await supabase
        .from("artworks")
        .update({ dossier_generated: true })
        .eq("id", artwork.id);
    } catch (err) {
      console.error("ArtworkDossierExport:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={generate}
      disabled={loading}
      className={className}
      style={!className ? {
        padding: "8px 16px",
        background: loading ? "#ccc" : "#1a1a1a",
        color: "#fff",
        border: "none",
        cursor: loading ? "not-allowed" : "pointer",
        fontSize: 13,
        fontFamily: "Georgia, serif",
        letterSpacing: "0.03em",
      } : undefined}
    >
      {loading ? "Génération…" : (label ?? "Exporter le dossier PDF")}
    </button>
  );
}
